import {useEffect, useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link} from 'react-router-dom'
import {useFormik} from 'formik'
import Swal from 'sweetalert2'
import {changePassword} from '../services'
import {InputPassword} from '../../../components/UI/Input/inputPassword'

const initialValues = {
  newPassword: '',
  checkNewPassword: '',
}

const forgotPasswordSchema = Yup.object().shape({
  newPassword: Yup.string()
    .min(8, 'Mínimo 8 caracteres')
    .max(15, 'Máximo 15 caracteres')
    .required('Contraseña es requerido.')
    .matches(
      /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[^\w\d\s])(.{8,15})$/,
      'Debe tener al menos 8 caracteres, una mayúscula, una minúscula, un número y un caracter especial.',
    ),
  checkNewPassword: Yup.string()
    .min(8, 'Mínimo 8 caracteres')
    .max(15, 'Máximo 15 caracteres')
    .oneOf([Yup.ref('newPassword'), null], 'Las contraseñas no coinciden')
    .required('Contraseña es requerido.'),
})

export function RecoverPassword() {
  const [loading, setLoading] = useState(false)
  const [token, setToken] = useState('')
  const [errorMsg, setErrorMsg] = useState('')
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)

  useEffect(() => {
    const urlSearchString = window.location.search
    const params = new URLSearchParams(urlSearchString)
    let token: string = params.get(`token`) || ''
    setToken(token)

    setTimeout(() => {
      checkToken(token)
    }, 100)
  }, [])

  const checkToken = (token_get: string) => {
    console.log('token >', token_get)
    if (token_get === '') {
      Swal.fire({
        heightAuto: false,
        text: 'El link ha expirado',
        icon: 'error',
        buttonsStyling: false,
        confirmButtonText: 'Ok',
        customClass: {
          confirmButton: 'btn fw-semobold btn-light-primary',
        },
      }).then(function () {
        window.location.href = '/central/auth'
      })
    }
  }

  const formik = useFormik({
    initialValues,
    validationSchema: forgotPasswordSchema,
    onSubmit: (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      setHasErrors(undefined)
      setTimeout(() => {
        changePassword(token, values.newPassword, values.checkNewPassword)
          .then(({data}) => {
            setLoading(false)
            setHasErrors(data.error)
            if (data.error) {
              setErrorMsg(data.message)
              setSubmitting(false)
            } else {
              Swal.fire({
                heightAuto: false,
                text: 'La contraseña ha sido cambiada correctamente. Ahora puedes iniciar sesión.',
                icon: 'success',
                buttonsStyling: false,
                confirmButtonText: 'Iniciar sesión',
                customClass: {
                  confirmButton: 'btn fw-semobold btn-light-primary',
                },
              }).then(function () {
                window.location.href = '/central/auth'
              })
            }
          })
          .catch(() => {
            setHasErrors(true)
            setLoading(false)
            setSubmitting(false)
            setStatus('Ups. algo selió mal.')
          })
      }, 1000)
    },
  })

  return (
    <form
      className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
      noValidate
      id='kt_login_password_reset_form'
      onSubmit={formik.handleSubmit}
    >
      <div className='text-center mb-10'>
        {/* begin::Title */}
        <h1 className='text-dark fw-bolder mb-3'>Crear Contraseña</h1>
        {/* end::Title */}

        {/* begin::Link */}
        <div className='text-gray-500 fw-semibold fs-6'>
          Se ha reseteado tu contraseña, por motivos de seguridad crea una nueva contraseña para
          ingresar a tu cuenta.
        </div>
        {/* end::Link */}
      </div>

      {/* begin::Title */}
      {hasErrors === true && (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>
            <h5 className={'text-danger'}>{errorMsg}</h5>
            Lo sentimos, parece que se han detectado algunos errores. Inténtalo de nuevo.
          </div>
        </div>
      )}

      {hasErrors === false && (
        <div className='mb-10 bg-light-info p-8 rounded'>
          <div className='text-info'>La contraseña ha sido cambiada correctamente.</div>
        </div>
      )}
      {/* end::Title */}

      <div className='fv-row mb-3'>
        <label className='form-label fw-bolder text-dark fs-6 mb-0'>Nueva contraseña</label>
        <InputPassword
          autoComplete='off'
          maxLength={15}
          {...formik.getFieldProps('newPassword')}
          className={clsx(
            'form-control bg-transparent',
            {
              'is-invalid': formik.touched.newPassword && formik.errors.newPassword,
            },
            {
              'is-valid': formik.touched.newPassword && !formik.errors.newPassword,
            },
          )}
        />
        {formik.touched.newPassword && formik.errors.newPassword && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.newPassword}</span>
            </div>
          </div>
        )}
      </div>

      <div className='fv-row mb-3'>
        <label className='form-label fw-bolder text-dark fs-6 mb-0'>
          Repetir tu nueva contraseña
        </label>
        <InputPassword
          autoComplete='off'
          maxLength={15}
          {...formik.getFieldProps('checkNewPassword')}
          className={clsx(
            'form-control bg-transparent',
            {
              'is-invalid': formik.touched.checkNewPassword && formik.errors.checkNewPassword,
            },
            {
              'is-valid': formik.touched.checkNewPassword && !formik.errors.checkNewPassword,
            },
          )}
        />
        {formik.touched.checkNewPassword && formik.errors.checkNewPassword && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.checkNewPassword}</span>
            </div>
          </div>
        )}
      </div>

      <div className='bg-light-danger d-flex align-items-center rounded p-5 mb-7'>
        <span className='svg-icon me-5 svg-icon-danger'>
          <span className='svg-icon svg-icon-1'>
            <svg
              fill='none'
              viewBox='0 0 24 24'
              height='24'
              width='24'
              xmlns='http://www.w3.org/2000/svg'
            >
              <rect
                xmlns='http://www.w3.org/2000/svg'
                opacity='0.3'
                x='2'
                y='2'
                width='20'
                height='20'
                rx='10'
                fill='currentColor'
              ></rect>
              <path
                xmlns='http://www.w3.org/2000/svg'
                d='M15.8054 11.639C15.6757 11.5093 15.5184 11.4445 15.3331 11.4445H15.111V10.1111C15.111 9.25927 14.8055 8.52784 14.1944 7.91672C13.5833 7.30557 12.8519 7 12 7C11.148 7 10.4165 7.30557 9.80547 7.9167C9.19432 8.52784 8.88885 9.25924 8.88885 10.1111V11.4445H8.66665C8.48153 11.4445 8.32408 11.5093 8.19444 11.639C8.0648 11.7685 8 11.926 8 12.1112V16.1113C8 16.2964 8.06482 16.4539 8.19444 16.5835C8.32408 16.7131 8.48153 16.7779 8.66665 16.7779H15.3333C15.5185 16.7779 15.6759 16.7131 15.8056 16.5835C15.9351 16.4539 16 16.2964 16 16.1113V12.1112C16.0001 11.926 15.9351 11.7686 15.8054 11.639ZM13.7777 11.4445H10.2222V10.1111C10.2222 9.6204 10.3958 9.20138 10.7431 8.85421C11.0903 8.507 11.5093 8.33343 12 8.33343C12.4909 8.33343 12.9097 8.50697 13.257 8.85421C13.6041 9.20135 13.7777 9.6204 13.7777 10.1111V11.4445Z'
                fill='currentColor'
              ></path>
            </svg>
          </span>
        </span>
        <div className='flex-grow-1 me-2'>
          <a href='#' className='fw-bold text-gray-800 text-hover-primary fs-6'>
            La contraseña debe contener:
          </a>
          <span className='text-muted fw-semobold d-block'>Al menos una mayúscula.</span>
          <span className='text-muted fw-semobold d-block'>Al menos una minúscula.</span>
          <span className='text-muted fw-semobold d-block'>Al menos un dígito.</span>
          <span className='text-muted fw-semobold d-block'>
            Al menos un caracter especial
            <i>(- * ? ! @ # $ / () {} = . , ; :)</i>.
          </span>
          <span className='text-muted fw-semobold d-block'>Longitud de 8 a 15 caracteres.</span>
        </div>
      </div>

      {/* begin::Form group */}
      <div className='d-flex flex-wrap justify-content-center pb-lg-0 py-10'>
        <button
          type='submit'
          id='kt_password_reset_submit'
          className='btn btn-primary me-4'
          disabled={formik.isSubmitting || !formik.isValid}
        >
          {!loading ? (
            <span className='indicator-label'> Cambiar mi contraseña </span>
          ) : (
            <span className='indicator-progress' style={{display: 'block'}}>
              Cambiando...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
        <Link to='/auth/login'>
          <button
            type='button'
            id='kt_login_password_reset_form_cancel_button'
            className='btn btn-light'
            disabled={formik.isSubmitting || !formik.isValid}
          >
            Cancelar
          </button>
        </Link>{' '}
      </div>
      {/* end::Form group */}
    </form>
  )
}
